import type { EventRow, Json } from '../../lib/database.types'
import { shortDate } from '../../lib/time'
import { faceFor } from '../../lib/glyph'
import { stripMarkers } from '../../lib/text'

/**
 * The changelog needs no schema. `agent.finished` already carries the agent's own first-person account of what it just did, in
 * payload.result.result, written without being asked for one.
 *
 * It is model prose of arbitrary length and shape, so it is rendered as text
 * with a clamp and no layout that assumes a number of lines. Nothing here tries
 * to parse it, summarise it, or make it consistent -- the point of the view is
 * that it is the agent's account rather than ours.
 */

/** The type that carries it. `claude.finished` until 24 Sep 2026. */
export const AGENT_FINISHED_TYPE = 'agent.finished'

export interface ChangelogEntry {
  id: number
  face: string
  agent: string
  department: string
  date: string
  body: string
  runId: string | null
  url: string | null
}

export function toChangelog(
  events: ReadonlyArray<EventRow>,
  runInfo: ReadonlyMap<string, { agent: string | null; url: string | null }>,
): ChangelogEntry[] {
  const out: ChangelogEntry[] = []
  for (const e of events) {
    if (e.type !== AGENT_FINISHED_TYPE) continue
    const payload = e.payload as Record<string, Json | undefined> | null
    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) continue
    const result = payload['result'] as Record<string, Json | undefined> | undefined
    const body = result && typeof result === 'object' && !Array.isArray(result) ? result['result'] : undefined
    if (typeof body !== 'string' || !body.trim()) continue
    const info = e.run_id ? runInfo.get(e.run_id) : undefined
    out.push({
      id: e.id,
      face: faceFor(info?.agent ?? null),
      agent: info?.agent ?? 'unknown agent',
      department: e.department,
      date: shortDate(e.ts),
      body: stripMarkers(body.trim()),
      runId: e.run_id,
      url: info?.url ?? null,
    })
  }
  return out
}
